import { MenuList, Paper, TextField, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { alpha, useTheme } from '@mui/material/styles';
import isArray from 'lodash/isArray';
import React, { forwardRef } from 'react';
import Select from 'react-select';

const Placeholder = props => (
  <Typography
    sx={{
      position: 'absolute',
      left: '10px',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontWeight: 'normal',
      fontStyle: 'normal',
      fontStretch: 'normal',
      letterSpacing: '-0.1px',
      color: 'text.secondary',
    }}
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);
const Menu = ({ innerProps, children }) => (
  <Paper
    elevation={8}
    sx={theme => ({
      position: 'absolute',
      zIndex: 1,
      mt: theme.spacing(0.5),
      left: 0,
    })}
    {...innerProps}
  >
    <MenuList disablePadding>{children}</MenuList>
  </Paper>
);

const Option = ({ innerProps, isDisabled, isSelected, children }) => {
  return (
    <MenuItem selected={isSelected} disabled={isDisabled} {...innerProps}>
      {children}
    </MenuItem>
  );
};

const inputComponent = ({ inputRef, ...props }) => <div ref={inputRef} {...props} />;

const controlInputStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const Control = ({ selectProps, children, innerProps }) => {
  return (
    <TextField
      fullWidth
      error={selectProps.error}
      helperText={selectProps.helperText}
      slotProps={{
        input: {
          inputComponent,
          inputProps: {
            children,
            ...innerProps,
            style: { ...controlInputStyle, ...innerProps.style },
          },
          ...selectProps.controlProps,
        },
      }}
    />
  );
};

const AutocompleteField = (
  {
    options,
    value,
    onChange,
    labelGenerator,
    findValue = (opts, val) => opts.find(option => option.value === val),
    ...props
  },
  ref,
) => {
  const theme = useTheme();
  const result = findValue(options, value);
  return (
    <Select
      ref={ref}
      styles={{
        container: provided => ({ ...provided, flexGrow: 1 }),
      }}
      options={options}
      value={
        value && !isArray(value)
          ? { label: result && labelGenerator(result), value }
          : value?.map(v => ({ label: labelGenerator(findValue(options, v)), value: v }))
      }
      onChange={onChange}
      components={{ Placeholder, Menu, Control, Option }}
      {...props}
      theme={th => ({
        ...th,
        colors: {
          ...th.colors,
          primary:
            theme.palette.mode === 'dark'
              ? theme.palette.primary.lighter
              : theme.palette.primary.main,
          primary75: theme.palette.primary.lighter,
          primary50: theme.palette.primary.extraLight,
          primary25: theme.palette.primary.ultraLight,
          neutral10: alpha(theme.palette.text.primary, 0.1),
          neutral15: alpha(theme.palette.text.primary, 0.15),
          neutral20: alpha(theme.palette.text.primary, 0.2),
          neutral30: alpha(theme.palette.text.primary, 0.3),
          neutral40: alpha(theme.palette.text.primary, 0.4),
          neutral50: alpha(theme.palette.text.primary, 0.5),
          neutral60: alpha(theme.palette.text.primary, 0.6),
          neutral70: alpha(theme.palette.text.primary, 0.7),
          neutral80: alpha(theme.palette.text.primary, 0.8),
          neutral90: alpha(theme.palette.text.primary, 0.9),
        },
      })}
    />
  );
};

export default forwardRef(AutocompleteField);
